import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { api } from '../../../services/api';
import { useNotification } from '../../../contexts/NotificationContext';

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
`;

const Badge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  background: #d1fae5;
  color: #065f46;
  font-size: 0.8rem;
  font-weight: 600;
`;

const Command = styled.code`
  display: block;
  background: #111827;
  color: #f9fafb;
  border-radius: 6px;
  padding: 0.9rem 1rem;
  margin: 1rem 0;
  overflow-x: auto;
`;

const Grant = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  padding: 1rem 0;
  border-top: 1px solid #e5e7eb;
`;

const RevokeButton = styled.button`
  align-self: center;
  background: transparent;
  border: 1px solid #dc2626;
  color: #dc2626;
  border-radius: 5px;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
`;

interface GrantData {
  id: string;
  client_name: string;
  authorized_by: string;
  scopes: string[];
  created_at: string;
  last_used_at?: string;
  revoked_at?: string;
  active: boolean;
}

const McpSettings: React.FC = () => {
  const [grants, setGrants] = useState<GrantData[]>([]);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();

  const load = useCallback(async () => {
    try {
      const response = await api.get('/mcp/oauth/grants');
      setGrants(response.data.items || []);
    } catch (error) {
      console.error('Erro ao carregar conexões MCP:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (grant: GrantData) => {
    if (!window.confirm(`Revogar a conexão ${grant.client_name}?`)) return;
    try {
      await api.post(`/mcp/oauth/grants/${grant.id}/revoke`);
      showNotification('Conexão MCP revogada', 'success');
      await load();
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Erro ao revogar conexão MCP', 'error');
    }
  };

  const active = grants.filter(grant => grant.active);
  return (
    <Card>
      <Header>
        <div>
          <h4 style={{ margin: 0 }}>🤖 Codex / MCP da iLaura</h4>
          <p style={{ color: '#666', marginBottom: 0 }}>
            Conecte o Codex sem copiar tokens ou compartilhar sua senha administrativa.
          </p>
        </div>
        <Badge>{active.length} {active.length === 1 ? 'conexão ativa' : 'conexões ativas'}</Badge>
      </Header>
      <p>Com o plugin instalado, execute:</p>
      <Command>codex mcp login imobiliaria</Command>
      <p style={{ color: '#666' }}>
        O navegador abrirá esta área para login e consentimento. Access tokens expiram em 15 minutos;
        a renovação é automática e rotativa.
      </p>
      {loading && <p>Carregando conexões…</p>}
      {!loading && active.length === 0 && <p>Nenhuma conexão MCP ativa.</p>}
      {active.map(grant => (
        <Grant key={grant.id}>
          <div>
            <strong>{grant.client_name}</strong><br />
            <small>Autorizado por {grant.authorized_by}</small><br />
            <small>Último uso: {grant.last_used_at ? new Date(grant.last_used_at).toLocaleString('pt-BR') : 'ainda não utilizado'}</small>
          </div>
          <RevokeButton onClick={() => revoke(grant)}>Revogar</RevokeButton>
        </Grant>
      ))}
    </Card>
  );
};

export default McpSettings;
